import React, { ReactNode } from 'react'
import { v4 as uuid } from 'uuid'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { IllustrationChoices } from '../../../components/common/Illustration'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { ButtonVariant } from '../../../components/common/Button'
import { SpinnerWrapper } from '../../../components/common/SpinnerWrapper'
import { ErrorView } from '../ErrorView'
import { useAuth } from '../../../hooks/useAuth'
import { societyQueries } from '../../../api/societies'
import { requestQueries } from '../../../api/requests'
import { i18nSocietyType } from '../../../i18n'

interface SocietyErrorViewWrapperProps {
  children: ReactNode
}

const SocietyErrorViewWrapper = ({
  children,
}: SocietyErrorViewWrapperProps): React.ReactNode => {
  const navigate = useNavigate()
  const { translate } = useAppTranslation()
  const { userId } = useAuth()
  const { data: sortedSocieties, isLoading: isLoadingSocieties } = useQuery(
    societyQueries.getUserSocieties(userId)
  )

  const { data: userRequests, isLoading: isLoadingRequests } = useQuery(
    requestQueries.getRequestsPerUser(userId)
  )

  const userHasNoSocieties = sortedSocieties && sortedSocieties.length === 0
  const userPendingRequests = userRequests

  const noSocietyErrorView =
    !isLoadingSocieties && userHasNoSocieties
      ? {
          title: translate('feedView.emptyState.noSociety.title', {
            context: i18nSocietyType(
              sortedSocieties?.[0]?.societyTypeDisplayName
            ),
          }),
          subtitle: translate('feedView.emptyState.noSociety.subtitle'),
          illustration: IllustrationChoices.EMPTY,
          buttons: [
            {
              id: uuid(),
              button: {
                label: translate('feedView.emptyState.noSociety.buttonTitle', {
                  context: i18nSocietyType(
                    sortedSocieties?.[0]?.societyTypeDisplayName
                  ),
                }),
                onClick: () => navigate(reverseUrl('find-society')),
                variant: ButtonVariant.DEFAULT,
              },
            },
            {
              id: uuid(),

              button: {
                label: translate(
                  'feedView.emptyState.noSociety.createSocietyButton',
                  {
                    context: i18nSocietyType(
                      sortedSocieties?.[0]?.societyTypeDisplayName
                    ),
                  }
                ),
                onClick: () => navigate(reverseUrl('create-society:pick-type')),
                variant: ButtonVariant.PRIMARY,
              },
            },
          ],
        }
      : undefined

  const pendingRequestErrorView =
    userPendingRequests &&
    userPendingRequests.length > 0 &&
    userHasNoSocieties &&
    !isLoadingSocieties
      ? {
          title: translate('societyView.emptyState.pendingRequest.title', {
            context: sortedSocieties?.[0]?.societyTypeDisplayName,
          }),
          subtitle: translate(
            'societyView.emptyState.pendingRequest.subtitle',
            { context: sortedSocieties?.[0]?.societyTypeDisplayName }
          ),
          illustration: IllustrationChoices.WAITING,
          buttons: [
            {
              id: uuid(),
              button: {
                label: translate(
                  'societyView.emptyState.pendingRequest.buttonTitle',
                  { context: sortedSocieties?.[0]?.societyTypeDisplayName }
                ),
                onClick: () => navigate(reverseUrl('find-society')),
                variant: ButtonVariant.PRIMARY,
              },
            },
          ],
        }
      : undefined

  if (isLoadingSocieties || isLoadingRequests)
    return <SpinnerWrapper className="mt-16" />
  if (pendingRequestErrorView) return <ErrorView {...pendingRequestErrorView} />
  if (noSocietyErrorView)
    return (
      <ErrorView
        {...noSocietyErrorView}
        buttonClassName="flex flex-wrap justify-center gap-4"
      />
    )
  return <>{children}</>
}

SocietyErrorViewWrapper.displayName = 'SocietyErrorViewWrapper'
export { SocietyErrorViewWrapper }
